import { html, render, type TemplateResult } from 'lit-html';
import { ApiError } from './api.js';
import type { UiConfig } from './auth.js';
import { icon } from './icons.js';

/** viewer reads; admin changes settings and manages viewers/admins; owner (super admin) also manages owners. */
export type Role = 'viewer' | 'admin' | 'owner';
export const ROLE_LABEL: Record<Role, string> = { viewer: 'Viewer', admin: 'Admin', owner: 'Owner' };

export interface Me {
  actor: string;
  role: Role;
  auth: 'iam' | 'sso' | 'dev';
}

export interface Page {
  title: string;
  adminOnly?: boolean;
  /** No data to load: render straight away without a loading state. */
  instant?: boolean;
  /** Loads data for the page; called on navigation and after actions. */
  load(): Promise<void>;
  view(): TemplateResult;
}

export const app = {
  config: null as unknown as UiConfig,
  me: null as unknown as Me,
  route: 'dashboard',
  /** Second hash segment, e.g. the guide section in #/guide/queues. */
  routeParam: '',
  loading: false,
  error: null as string | null,
  toasts: [] as { id: number; text: string; tone: string }[],
  confirm: null as null | {
    title: string;
    body: TemplateResult | string;
    confirmLabel: string;
    tone: 'danger' | 'primary';
    reasonField?: boolean;
    resolve: (r: { ok: boolean; reason?: string }) => void;
  },
  rerender: () => undefined as void,
};

export const isAdmin = () => app.me?.role === 'admin' || app.me?.role === 'owner';
export const isOwner = () => app.me?.role === 'owner';

let toastSeq = 0;
export function toast(text: string, tone: 'positive' | 'critical' | 'info' = 'positive'): void {
  const id = ++toastSeq;
  app.toasts.push({ id, text, tone });
  app.rerender();
  setTimeout(() => {
    app.toasts = app.toasts.filter((t) => t.id !== id);
    app.rerender();
  }, tone === 'critical' ? 8000 : 4000);
}

export function confirmDialog(opts: {
  title: string;
  body: TemplateResult | string;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  reasonField?: boolean;
}): Promise<{ ok: boolean; reason?: string }> {
  return new Promise((resolve) => {
    app.confirm = { ...opts, tone: opts.tone ?? 'primary', resolve };
    app.rerender();
  });
}

/** Runs an action, reports its outcome as a toast, and reloads the current page. */
export async function act(fn: () => Promise<unknown>, success: string, reload: () => Promise<void>): Promise<boolean> {
  try {
    await fn();
    toast(success);
    await reload();
    app.rerender();
    return true;
  } catch (err) {
    const e = err as ApiError;
    const detail = e.issues?.length ? `: ${e.issues.map((i) => `${i.path || 'value'} ${i.message}`).join('; ')}` : '';
    toast(`${e.message}${detail}`, 'critical');
    return false;
  }
}

export function formValues(form: HTMLFormElement): Record<string, string> {
  const out: Record<string, string> = {};
  new FormData(form).forEach((v, k) => {
    out[k] = String(v);
  });
  return out;
}

export function mount(root: HTMLElement, view: () => TemplateResult): void {
  app.rerender = () => render(view(), root);
}

export function emptyRow(cols: number, text: string): TemplateResult {
  return html`<tr><td colspan=${cols} class="empty">${text}</td></tr>`;
}

/** Small "Help" link from a page heading to its section of the in-app guide. */
export function helpLink(section: string): TemplateResult {
  return html`<a class="btn quiet compact help-link" href="#/guide/${section}" title="How this page works">${icon('guide')}Help</a>`;
}
